import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  BookOpen,
  HelpCircle,
  BarChart3,
  Library,
  Trophy,
  Settings,
  Menu,
  X,
  LogOut,
  Zap,
  Flame,
  Snowflake,
  CloudOff,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

interface LayoutProps {
  children: React.ReactNode;
  stats?: {
    new_count: number;
    learning_count: number;
    mastered_count: number;
    xp: number;
    current_streak: number;
    words_studied_today: number;
    daily_goal: number;
    streak_freezes?: number;
  };
  pendingCount: number;
  isOnline: boolean;
}

const baseNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/study', label: 'Study', icon: BookOpen },
  { path: '/quiz', label: 'Quiz', icon: HelpCircle },
  { path: '/words', label: 'Words', icon: Library },
  { path: '/progress', label: 'Progress', icon: BarChart3 },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  { path: '/settings', label: 'Settings', icon: Settings },
];

// Bottom nav fits five items; the rest stay in the hamburger menu.
const mobileBottomNavPaths = ['/dashboard', '/study', '/quiz', '/words', '/progress'];

const adminNavItem = { path: '/admin', label: 'Admin', icon: Shield };

export const Layout: React.FC<LayoutProps> = ({ children, stats, pendingCount, isOnline }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const dailyGoalPercent = stats
    ? Math.min((stats.words_studied_today / stats.daily_goal) * 100, 100)
    : 0;

  const navItems = user?.role === 'admin'
    ? [...baseNavItems, adminNavItem]
    : baseNavItems;

  return (
    <div className="h-screen flex flex-col md:grid md:grid-cols-[220px_1fr] md:grid-rows-[56px_1fr] bg-dark text-text overflow-hidden">
      {/* Header */}
      <header className="md:col-span-2 flex items-center justify-between px-5 h-14 shrink-0 z-20 relative"
        style={{
          background: 'rgba(8,11,20,0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid transparent',
          borderImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08) 20%, rgba(255,255,255,0.08) 80%, transparent) 1',
        }}
      >
        <div className="absolute bottom-[-1px] left-0 right-0 h-px pointer-events-none"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(108,99,255,0.15) 50%, transparent)' }}
        />

        <Link to="/dashboard" className="font-display text-lg font-bold text-gold-text tracking-tight shrink-0">
          GRE <span className="text-cream italic font-normal">Vocab Studio</span>
        </Link>

        {/* Desktop stats bar */}
        {stats && !isMobile && (
          <div className="flex items-center gap-1.5 flex-1 justify-center">
            <StatPill label="New" count={stats.new_count} dotColor="bg-text-dim" />
            <StatPill label="Learning" count={stats.learning_count} dotColor="bg-amber" />
            <StatPill label="Mastered" count={stats.mastered_count} dotColor="bg-green" />
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {/* Offline sync indicator */}
          {(!isOnline || pendingCount > 0) && (
            <div className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-xs font-medium ${!isOnline ? 'text-amber' : 'text-text-dim'}`}>
              {!isOnline ? <CloudOff size={12} /> : <RefreshCw size={12} className="animate-spin" />}
              <span>
                {!isOnline
                  ? `Offline - ${pendingCount} pending`
                  : pendingCount > 0
                  ? `Syncing...`
                  : ''}
              </span>
            </div>
          )}

          {stats && (
            <div className="hidden md:flex items-center gap-3">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-xs text-text-hi font-medium">
                <Zap size={13} className="text-gold" />
                <span>{stats.xp} XP</span>
              </div>
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-xs text-text-hi font-medium"
                title={`${stats.current_streak}-day streak${stats.streak_freezes ? ` · ${stats.streak_freezes} streak freeze${stats.streak_freezes > 1 ? 's' : ''} available` : ''}`}
              >
                <Flame size={13} className="text-amber" />
                <span>{stats.current_streak}</span>
                {(stats.streak_freezes ?? 0) > 0 && (
                  <span className="flex items-center gap-0.5 text-blue">
                    <Snowflake size={11} />
                    {stats.streak_freezes}
                  </span>
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full glass text-xs text-text-hi font-medium">
              <span>{user.username}</span>
              <span className="text-[9px] font-semibold uppercase tracking-widest text-gold px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--gold-glow)', border: '1px solid var(--gold-dim)' }}
              >
                {user.title || 'Novice'}
              </span>
            </div>
          )}

          <button
            onClick={handleLogout}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-surface text-text-dim hover:text-gold hover:border-gold-dim hover:bg-gold-glow transition-all duration-150"
            aria-label="Logout"
          >
            <LogOut size={16} />
          </button>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-surface text-text-dim hover:text-gold transition-all duration-150"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside className="hidden md:block overflow-y-auto py-4"
        style={{
          background: 'rgba(8,11,20,0.7)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRight: '1px solid var(--border-color)',
        }}
      >
        <nav className="space-y-1 px-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'text-gold-text bg-gradient-to-r from-gold-glow to-transparent'
                    : 'text-text-dim hover:text-text-hi hover:bg-surface-1'
                }`}
                style={isActive ? { boxShadow: 'inset 0 0 0 1px var(--gold-dim)' } : {}}
              >
                <Icon size={16} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-8 px-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-dim">Daily Goal</span>
            <span className="text-[10px] font-mono text-text-dim">
              {stats?.words_studied_today ?? 0}/{stats?.daily_goal ?? 20}
            </span>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${dailyGoalPercent}%`,
                background: 'var(--green)',
                boxShadow: '0 0 6px rgba(61,158,120,0.5)',
              }}
            />
          </div>
        </div>
      </aside>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="md:hidden fixed left-0 top-0 bottom-0 w-64 z-40 p-4"
              style={{
                background: 'rgba(8,11,20,0.95)',
                backdropFilter: 'blur(24px)',
                borderRight: '1px solid var(--border-color)',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-display text-lg font-bold text-gold-text">Menu</span>
                <button onClick={() => setMobileMenuOpen(false)} className="text-text-dim hover:text-text-hi">
                  <X size={20} />
                </button>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'text-gold-text bg-gradient-to-r from-gold-glow to-transparent'
                          : 'text-text-dim hover:text-text-hi hover:bg-surface-1'
                      }`}
                      style={isActive ? { boxShadow: 'inset 0 0 0 1px var(--gold-dim)' } : {}}
                    >
                      <Icon size={18} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
              {user && (
                <div className="mt-8 pt-6 border-t border-border">
                  <div className="flex items-center gap-2 px-4 mb-4">
                    <span className="text-sm text-text-hi">{user.username}</span>
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-gold px-1.5 py-0.5 rounded-full"
                      style={{ background: 'var(--gold-glow)', border: '1px solid var(--gold-dim)' }}
                    >
                      {user.title || 'Novice'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-red hover:bg-red-muted transition-all w-full"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden relative">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-20 flex items-center justify-around px-2"
        style={{
          background: 'rgba(8,11,20,0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: '1px solid var(--border-color)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {navItems
          .filter((item) => mobileBottomNavPaths.includes(item.path))
          .map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 w-14 h-14 rounded-xl transition-all duration-150 ${
                  isActive ? 'text-gold-text' : 'text-text-dim'
                }`}
              >
                <Icon size={20} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
      </nav>
    </div>
  );
};

const StatPill: React.FC<{ label: string; count: number; dotColor: string }> = ({ label, count, dotColor }) => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-xs">
    <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
    <span className="text-text-dim font-medium">{label}</span>
    <span className="text-text-hi font-semibold">{count}</span>
  </div>
);
